import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from '@tanstack/react-router'
import clsx from 'clsx'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import { buildV3Url } from '~/utils/legacy'
import { Tooltip } from '../../Global/Elements/Tooltip/Tooltip'
import {
  item,
  itemExtended,
  itemTrigger,
  itemTriggerActive,
  itemTriggerExtended,
  itemTriggerIcon,
  subItemBeta,
  subItemIcon,
  subitem,
  submenu,
  subtitle
} from './GlobalMenu.css'
import type { MenuItem, MenuItemPrimary } from './menu'

interface GlobalMenuItemProps {
  menuItem: MenuItemPrimary
  currentSection: string | null
  extendedMenu: boolean
  legacyUrl?: string | null
  onOrderCreateClick?: () => void
  onRecourseCreateClick?: () => void
}

export const GlobalMenuItem = ({
  menuItem,
  currentSection,
  extendedMenu,
  legacyUrl,
  onOrderCreateClick,
  onRecourseCreateClick
}: GlobalMenuItemProps): ReactElement => {
  const [isOpen, setOpen] = useState<boolean>(false)
  const [timer, setTimer] = useState<number | null>(null)
  const submenuRef = useRef<HTMLDivElement | null>(null)

  useEffect((): void => {
    const current = submenuRef.current
    if (
      isOpen &&
      current !== null &&
      current.getBoundingClientRect().bottom > window.innerHeight
    ) {
      current.style.top = 'auto'
      current.style.bottom = '0px'
    } else if (isOpen && current !== null) {
      current.style.bottom = 'auto'
      current.style.top = '-40px'
    }
  }, [isOpen])

  const onOpening = (): void => {
    setOpen(true)
    if (timer !== null) {
      window.clearTimeout(timer)
    }
  }

  const onClosing = (): void => {
    setTimer(
      window.setTimeout(() => {
        setOpen(false)
      }, 100)
    )
  }

  const buildLinkItem = (menuItem: MenuItem, subItem = false) => {
    const isOrderCreateLink = menuItem.id === 'create_order'
    const isRecourseCreateLink = menuItem.id === 'recours'
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isOrderCreateLink && onOrderCreateClick) {
        e.preventDefault()
        onOrderCreateClick()
      } else if (isRecourseCreateLink && onRecourseCreateClick) {
        e.preventDefault()
        onRecourseCreateClick()
      }
    }

    let LinkItem = (
      <a
        key={menuItem.id + subItem}
        className={clsx(
          subItem ? subitem : null,
          itemTrigger,
          (isOpen && !subItem) ||
            currentSection === menuItem.id ||
            (menuItem.url && currentSection?.startsWith(menuItem.url))
            ? itemTriggerActive
            : null,
          extendedMenu ? itemTriggerExtended : null
        )}
        href={
          !menuItem.v3
            ? (legacyUrl ?? '') + (menuItem.url ?? '#')
            : buildV3Url(`${menuItem.url ?? '#'}`)
        }
        title={menuItem.label}
        onClick={menuItem.onClick ?? handleClick}
      >
        {menuItem.isBeta && <span className={subItemBeta}>Beta</span>}
        {menuItem.icon !== undefined && (
          <FontAwesomeIcon
            icon={menuItem.icon}
            className={clsx(
              subItem ? subItemIcon : undefined,
              extendedMenu ? itemTriggerIcon : undefined
            )}
          />
        )}
        {(subItem || extendedMenu) && menuItem.label}
      </a>
    )

    if (menuItem.v3 && window.JobConfig === undefined) {
      const handleLinkClick = (e: React.MouseEvent) => {
        if (isOrderCreateLink && onOrderCreateClick) {
          e.preventDefault()
          onOrderCreateClick()
        } else if (isRecourseCreateLink && onRecourseCreateClick) {
          e.preventDefault()
          onRecourseCreateClick()
        }
      }

      LinkItem = (
        <Link
          key={menuItem.id + subItem}
          to={menuItem.url ?? '#'}
          title={menuItem.label}
          className={clsx(
            itemTrigger,
            subItem ? subitem : null,
            (isOpen && !subItem) ||
              currentSection === menuItem.id ||
              currentSection?.startsWith(menuItem.url ?? '')
              ? itemTriggerActive
              : null,
            extendedMenu ? itemTriggerExtended : null
          )}
          onClick={handleLinkClick}
        >
          {menuItem.isBeta && <span className={subItemBeta}>Beta</span>}
          {menuItem.icon !== undefined && (
            <FontAwesomeIcon
              icon={menuItem.icon}
              className={subItem ? subItemIcon : undefined}
            />
          )}
          {(subItem || extendedMenu) && menuItem.label}
        </Link>
      )
    }

    return LinkItem
  }

  return (
    <>
      {menuItem.submenu !== undefined && (
        <div
          onMouseEnter={onOpening}
          onMouseLeave={onClosing}
          className={clsx(item, extendedMenu ? itemExtended : null)}
        >
          {buildLinkItem(menuItem)}
          {isOpen && (
            <div className={submenu} ref={submenuRef} key={menuItem.id}>
              <div className={subtitle}>{menuItem.label}</div>
              {menuItem.submenu.map((sub) => buildLinkItem(sub, true))}
            </div>
          )}
        </div>
      )}
      {menuItem.submenu === undefined && (
        <div className={clsx(item, extendedMenu ? itemExtended : null)}>
          <Tooltip
            asChild
            direction="right"
            text={extendedMenu ? null : menuItem.label}
          >
            <div className={itemTrigger}>{buildLinkItem(menuItem)}</div>
          </Tooltip>
        </div>
      )}
    </>
  )
}
